import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    Box,
    Typography,
    Button,
    CircularProgress,
} from '@mui/material';
import { Visibility as ViewIcon, Edit as EditIcon, Delete as DeleteIcon, ExpandMore as LoadMoreIcon } from '@mui/icons-material';
import type { TenantWithStatus } from '../types';
import { TenantListSkeleton } from './TenantListSkeleton';

export interface TenantListProps {
    tenants: TenantWithStatus[];
    loading: boolean;
    loadingMore?: boolean;
    onEdit: (tenant: TenantWithStatus) => void;
    onDelete: (tenant: TenantWithStatus) => void;
    onViewDetails: (tenant: TenantWithStatus) => void;
    onLoadMore?: () => void;
    hasMore: boolean;
}

export function TenantList({ tenants, loading, loadingMore = false, onEdit, onDelete, onViewDetails, onLoadMore, hasMore }: TenantListProps) {
    const formatDate = (dateString: string) => {
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return 'Invalid Date';
        }
    };

    // Show loading skeleton when initially loading
    if (loading && tenants.length === 0) {
        return <TenantListSkeleton rows={5} />;
    }

    // Show empty state when no tenants and not loading
    if (tenants.length === 0 && !loading) {
        return (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                    No tenants found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    There are no tenants to display. Create your first tenant to get started.
                </Typography>
            </Paper>
        );
    }

    return (
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Sub Domain</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Region</TableCell>
                        <TableCell align="right">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tenants.map((tenant) => (
                        <TableRow key={tenant.tenantId} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                    {tenant.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {tenant.tenantId}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2" fontFamily="monospace">
                                    {tenant.subDomain}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2">{formatDate(tenant.createdAt)}</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2">{tenant.region}</Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Tooltip title="View Details">
                                        <IconButton size="small" onClick={() => onViewDetails(tenant)} color="primary">
                                            <ViewIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Edit Tenant">
                                        <IconButton size="small" onClick={() => onEdit(tenant)} color="primary">
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete Tenant">
                                        <IconButton size="small" onClick={() => onDelete(tenant)} color="error">
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {/* Load More Section */}
            {(hasMore || loadingMore) && (
                <Box sx={{ p: 2, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
                    {loadingMore ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            <CircularProgress size={20} />
                            <Typography variant="body2" color="text.secondary">
                                Loading more tenants...
                            </Typography>
                        </Box>
                    ) : (
                        <Button variant="outlined" onClick={onLoadMore} startIcon={<LoadMoreIcon />} disabled={!hasMore}>
                            Load More Tenants
                        </Button>
                    )}
                </Box>
            )}
        </TableContainer>
    );
}
